import {
	FormModelFieldSubForm,
	isValidMidSpanIkePhotoField,
	isValidSpanTypeField,
	isValidSpanVectorField,
} from '@ikegps/core-shared';
import { m, type MithrilVNode } from '../../../mithril';
import { SelectInputComponent, type SelectInputOption, SelectInputState } from '../../../components/input/input.select';
import { IkeInputStateGroup } from '../../../components/input/input.state.group';
import { action, makeObservable } from 'mobx';
import { T, Translate } from '../../../i18n';
import { MobxState } from '../../../state/mobx';

const UNSELECTED_VALUE = '__UNSELECTED__';

export class SpanSubFormTypeEditor {
	private vectorFieldSelectState: SelectInputState;
	private typeFieldSelectState: SelectInputState;
	private midSpanPhotoFieldSelectState: SelectInputState;
	private reactions: MobxState;

	constructor(
		private field: FormModelFieldSubForm,
		private stateGroup: IkeInputStateGroup
	) {
		makeObservable(this);
		this.reactions = new MobxState();

		const spanState = this.field.subFormSpanTypeState;

		// 1. Create all three select states with current options and values
		this.vectorFieldSelectState = this.createVectorFieldSelect(spanState?.vectorFieldId ?? null);
		this.typeFieldSelectState = this.createTypeFieldSelect(spanState?.typeFieldId ?? null);
		this.midSpanPhotoFieldSelectState = this.createMidSpanPhotoFieldSelect(spanState?.midSpanIkePhotoFieldId ?? null);

		// 2. Set isRequired AFTER values are set, so the auto-select reaction
		//    in SelectInputState sees a truthy value and returns early
		this.vectorFieldSelectState.isRequired = true;
		this.typeFieldSelectState.isRequired = true;
		this.midSpanPhotoFieldSelectState.isRequired = true;

		// 3. Register field-change reactions that update options when subform fields change
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidSpanVectorField)
					.map((f) => f.displayName)
					.join(','),
			() => this.updateVectorFieldOptions()
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidSpanTypeField)
					.map((f) => f.displayName)
					.join(','),
			() => this.updateTypeFieldOptions()
		);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter(isValidMidSpanIkePhotoField)
					.map((f) => f.displayName)
					.join(','),
			() => this.updateMidSpanPhotoFieldOptions()
		);

		// 4. Register onValue handlers UNCONDITIONALLY
		//    for existing SPAN forms, these fire when the user changes a dropdown.
		this.vectorFieldSelectState.onValue((value) => {
			if (value) {
				this.field.Mutator.span.selectVectorField(value);
			}
		});
		this.typeFieldSelectState.onValue((value) => {
			if (value) {
				this.field.Mutator.span.selectSpanTypeField(value);
			}
		});
		this.midSpanPhotoFieldSelectState.onValue((value) => {
			if (value && value !== UNSELECTED_VALUE) {
				// Remove the unselected placeholder option when a real value is chosen
				const filteredOptions = this.midSpanPhotoFieldSelectState.options
					.filter((opt) => opt.value !== UNSELECTED_VALUE)
					.slice();
				this.midSpanPhotoFieldSelectState.setOptions(filteredOptions);
				this.midSpanPhotoFieldSelectState.setError(null);
				this.field.Mutator.span.selectMidSpanIkePhotoField(value);
			}
		});
	}

	private createVectorFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter(isValidSpanVectorField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		const state = new SelectInputState(Translate(T.SPAN_VECTOR_FIELD), options);
		state.setValue(currentValue);
		this.stateGroup.add(state);
		state.isLabelShown = true;

		return state;
	}

	private createTypeFieldSelect(currentValue: string | null): SelectInputState {
		const options = this.field.fields
			.filter(isValidSpanTypeField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		const state = new SelectInputState(Translate(T.SPAN_TYPE_FIELD), options);
		state.setValue(currentValue);
		this.stateGroup.add(state);
		state.isLabelShown = true;

		return state;
	}

	private createMidSpanPhotoFieldSelect(currentValue: string | null): SelectInputState {
		const options: SelectInputOption[] = this.field.fields
			.filter(isValidMidSpanIkePhotoField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		// Add unselected placeholder if this is an existing SPAN without the field set
		const needsUnselectedOption = !currentValue;
		if (needsUnselectedOption) {
			options.unshift({
				key: Translate(T.SELECT_OPTION),
				value: UNSELECTED_VALUE,
			});
		}

		const state = new SelectInputState(Translate(T.SPAN_MID_SPAN_IKE_PHOTO_FIELD), options);

		if (currentValue) {
			state.setValue(currentValue);
		} else if (needsUnselectedOption) {
			state.setValue(UNSELECTED_VALUE);
			state.setError(Translate(T.INPUT_REQUIRED));
			state.setTouched();
		}

		this.stateGroup.add(state);
		state.isLabelShown = true;

		return state;
	}

	@action private updateVectorFieldOptions() {
		if (!this.vectorFieldSelectState) return;

		const options = this.field.fields
			.filter(isValidSpanVectorField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		this.vectorFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === this.vectorFieldSelectState.value)) {
			this.vectorFieldSelectState.setValue(null);
		}
	}

	@action private updateTypeFieldOptions() {
		if (!this.typeFieldSelectState) return;

		const options = this.field.fields
			.filter(isValidSpanTypeField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		this.typeFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === this.typeFieldSelectState.value)) {
			this.typeFieldSelectState.setValue(null);
		}
	}

	@action private updateMidSpanPhotoFieldOptions() {
		if (!this.midSpanPhotoFieldSelectState) return;

		const currentValue = this.midSpanPhotoFieldSelectState.value;

		const options: SelectInputOption[] = this.field.fields
			.filter(isValidMidSpanIkePhotoField)
			.map((field) => ({ key: field.displayName, value: field.id }));

		// Preserve the UNSELECTED placeholder if it's the current value (fixes Bug 1)
		if (currentValue === UNSELECTED_VALUE) {
			options.unshift({
				key: Translate(T.SELECT_OPTION),
				value: UNSELECTED_VALUE,
			});
		}

		this.midSpanPhotoFieldSelectState.setOptions(options);

		if (!options.find((opt) => opt.value === currentValue)) {
			this.midSpanPhotoFieldSelectState.setValue(null);
		}
	}

	hasNoValidMidSpanPhotoOptions(): boolean {
		return this.field.fields.filter(isValidMidSpanIkePhotoField).length === 0;
	}

	@action createMidSpanPhotoField() {
		const field = this.field.Mutator.span.addMidSpanPhotoField();

		const options = this.field.fields
			.filter(isValidMidSpanIkePhotoField)
			.map((f) => ({ key: f.displayName, value: f.id }));
		this.midSpanPhotoFieldSelectState.setOptions(options);
		this.midSpanPhotoFieldSelectState.setValue(field.id);
		this.midSpanPhotoFieldSelectState.setError(null);
	}

	view(): MithrilVNode {
		return m('div', {}, [
			SelectInputComponent.view(this.vectorFieldSelectState),
			SelectInputComponent.view(this.typeFieldSelectState),
			m('div', { style: { display: 'flex', alignItems: 'center' } }, [
				SelectInputComponent.view(this.midSpanPhotoFieldSelectState),
				this.hasNoValidMidSpanPhotoOptions()
					? m(
							'button',
							{
								className: 'mdl-button',
								style: { marginLeft: '16px' },
								onclick: () => this.createMidSpanPhotoField(),
							},
							Translate(T.FORM_FIELD_CREATE)
						)
					: null,
			]),
		]);
	}

	destroy() {
		this.reactions.destroy();

		// Remove select states from the parent state group
		this.stateGroup.remove(this.vectorFieldSelectState);
		this.stateGroup.remove(this.typeFieldSelectState);
		this.stateGroup.remove(this.midSpanPhotoFieldSelectState);
	}
}
